import { useSignal } from '@preact/signals';
import { useEffect } from 'preact/hooks';

import type { AuthContext, EmailCodeChallenge } from '@amumin/api-client';
import { t } from '@amumin/i18n';
import { Button, Checkbox, Notice } from '@amumin/ui';

import { api, errorMessage, isApiError } from '../api.js';
import { formatInstantDate } from '../lib/dates.js';
import { normalizeCode, validateCode } from '../lib/validation.js';
import { appText, messages } from '../state/session.js';

export interface CodeEntryProps {
  challenge: EmailCodeChallenge;
  /** Uitleg boven het veld (login of netwerk-bevestiging). */
  intro: string;
  /** Vinkje "vertrouw deze browser 30 dagen" (alleen bij login). */
  showTrust?: boolean;
  onVerified: (ctx: AuthContext) => void;
  /** Bij een geblokkeerde of verlopen challenge: terug naar het begin. */
  onAbandon?: (() => void) | undefined;
  abandonLabel?: string | undefined;
  /** Link "Dit was ik niet" (uit de mail); hier alleen als tekst-hint. */
  showNotMeHint?: boolean;
  /**
   * 0065: een ander endpoint voor de code (aanmelden: /auth/signup/verify). Zonder: de gewone
   * inlogcode (/auth/challenge/verify).
   */
  verify?: (code: string, trustThisBrowser: boolean) => Promise<{ context: AuthContext }>;
}

/**
 * 6-cijferige e-mailcode (ONTWERP.md §11.1.2): één live code, resend met aftelling, max 5
 * pogingen. Alle purposes (browser_trust, reauth, network_confirm) lopen via challenge/verify.
 */
export function CodeEntry({
  challenge,
  intro,
  showTrust = false,
  onVerified,
  onAbandon,
  abandonLabel,
  showNotMeHint = false,
  verify,
}: CodeEntryProps) {
  const m = messages.value;
  const code = useSignal('');
  const trust = useSignal(true);
  const error = useSignal<string | undefined>(undefined);
  const notice = useSignal<string | undefined>(undefined);
  const busy = useSignal(false);
  const locked = useSignal(false);
  const resendLeft = useSignal(challenge.resend_after_s);

  useEffect(() => {
    resendLeft.value = challenge.resend_after_s;
    const timer = window.setInterval(() => {
      if (resendLeft.value > 0) resendLeft.value -= 1;
    }, 1000);
    return () => window.clearInterval(timer);
  }, [challenge.expires_at, challenge.resend_after_s, resendLeft]);

  const submit = async () => {
    const invalid = validateCode(code.value);
    if (invalid) {
      error.value = m.web[invalid];
      return;
    }
    busy.value = true;
    error.value = undefined;
    try {
      const res = verify
        ? await verify(normalizeCode(code.value), showTrust ? trust.value : true)
        : await api.app.auth.challengeVerify({
            code: normalizeCode(code.value),
            trust_this_browser: showTrust ? trust.value : false,
          });
      onVerified(res.context);
    } catch (e) {
      if (isApiError(e, 401, 'invalid_code') || isApiError(e, 400, 'invalid_code')) {
        const left = (e.details as { attempts_left?: number } | undefined)?.attempts_left;
        error.value =
          typeof left === 'number' ? t(m.web.codeWrong, { count: left }) : m.web.codeWrongPlain;
      } else if (isApiError(e, undefined, 'challenge_locked')) {
        error.value = m.web.codeLocked;
        locked.value = true;
      } else if (isApiError(e, undefined, 'challenge_expired') || isApiError(e, 401)) {
        error.value = m.web.codeExpired;
        locked.value = true;
      } else {
        error.value = errorMessage(e, m);
      }
    } finally {
      busy.value = false;
    }
  };

  const resend = async () => {
    if (resendLeft.value > 0 || busy.value) return;
    busy.value = true;
    error.value = undefined;
    try {
      await api.app.auth.challengeResend();
      notice.value = m.web.resent;
      resendLeft.value = challenge.resend_after_s;
    } catch (e) {
      error.value = errorMessage(e, m);
    } finally {
      busy.value = false;
    }
  };

  const trustUntil = new Date(Date.now() + 30 * 24 * 3600 * 1000).toISOString();

  return (
    <form
      data-name="code-entry"
      onSubmit={(e) => {
        e.preventDefault();
        void submit();
      }}
    >
      <p>{intro}</p>
      {notice.value ? <Notice kind="success">{notice.value}</Notice> : null}
      {error.value ? <Notice kind="error">{error.value}</Notice> : null}
      {/* Zes grote vakjes (DESIGN 8.W12, 56 px): één echt invoerveld eronder, zodat plakken,
          autofill (one-time-code) en schermlezers gewoon werken; de vakjes tonen de cijfers. */}
      <label class="otp-label" for="code">
        {m.web.codeLabel}
      </label>
      <div class={`otp${locked.value ? ' otp-locked' : ''}`} data-name="code-boxes">
        <input
          id="code"
          class="otp-input"
          type="text"
          inputMode="numeric"
          autoComplete="one-time-code"
          value={code.value}
          onInput={(e) =>
            (code.value = normalizeCode((e.currentTarget as HTMLInputElement).value).slice(0, 6))
          }
          maxLength={7}
          autoFocus
          disabled={locked.value}
          required
          aria-invalid={error.value ? 'true' : undefined}
        />
        <div class="otp-boxes" aria-hidden="true">
          {[0, 1, 2, 3, 4, 5].map((i) => (
            <span
              key={i}
              class={`otp-box${i === Math.min(code.value.length, 5) ? ' otp-box-next' : ''}`}
            >
              {code.value.charAt(i)}
            </span>
          ))}
        </div>
      </div>
      {showTrust ? (
        <Checkbox
          id="trust-browser"
          label={m.web.trustBrowser}
          checked={trust.value}
          onChange={(v) => (trust.value = v)}
          hint={t(m.web.trustBrowserHint, { date: formatInstantDate(trustUntil, m) })}
        />
      ) : null}
      {/* Eén primaire knop over de volle breedte, zoals Inloggen; opnieuw sturen rustig eronder
          (na 30 s). */}
      <Button
        name="code-confirm"
        type="submit"
        variant="primary"
        size="large"
        block
        busy={busy.value}
        disabled={locked.value}
      >
        {m.common.confirm}
      </Button>
      <div class="code-secondary">
        {/* Aftellen als gewone, leesbare tekst (--text-muted), niet als grijze knoptekst
            (contrast; ronde 3). Daarna pas de knop. */}
        {resendLeft.value > 0 ? (
          <span class="muted small" data-name="code-resend-wait">
            {t(m.web.resendIn, { seconds: resendLeft.value })}
          </span>
        ) : (
          <Button
            name="code-resend"
            variant="link"
            onPress={() => void resend()}
            disabled={locked.value}
          >
            {m.web.resend}
          </Button>
        )}
        {onAbandon ? (
          <Button name="code-abandon" variant="link" onPress={onAbandon}>
            {abandonLabel ?? m.common.back}
          </Button>
        ) : null}
      </div>
      {showNotMeHint ? (
        <p class="small muted">{t(appText.value.omgeving.notMeHint, { link: m.web.notMeLink })}</p>
      ) : null}
    </form>
  );
}
